import { getCLS, getFID, getFCP, getLCP, getTTFB } from 'web-vitals';
import { logAnalyticsEvent, ANALYTICS_EVENTS, trackGtagEvent } from '@/utils/Analytics';

// Web Vitals thresholds for Google Ads Quality Score optimization
const WEB_VITALS_THRESHOLDS = {
  LCP: { good: 2500, needsImprovement: 4000 }, // Largest Contentful Paint
  FID: { good: 100, needsImprovement: 300 },   // First Input Delay
  CLS: { good: 0.1, needsImprovement: 0.25 },  // Cumulative Layout Shift
  FCP: { good: 1800, needsImprovement: 3000 }, // First Contentful Paint
  TTFB: { good: 800, needsImprovement: 1800 }  // Time to First Byte
};

interface WebVitalMetric {
  name: string;
  value: number;
  id: string;
  rating: 'good' | 'needs-improvement' | 'poor';
}

// Send Web Vitals to both Google Analytics and custom analytics
function sendToAnalytics(metric: WebVitalMetric) {
  const { name, value, id, rating } = metric;
  
  // Send to Google Analytics via gtag. The event name is the metric (CLS/LCP/…)
  // by design, so this stays on the dynamic-name gtag path, not logAnalyticsEvent.
  trackGtagEvent(name, {
    event_category: 'Web Vitals',
    event_label: id,
    value: Math.round(name === 'CLS' ? value * 1000 : value),
    non_interaction: true,
    metric_rating: rating,
  });
  
  // Send to Firebase Analytics
  logAnalyticsEvent(ANALYTICS_EVENTS.WEB_VITAL_RECORDED, {
    metric: name,
    value,
    rating,
    metricId: id,
    timestamp: new Date().toISOString()
  });
  
  // DEV only: initializeWebVitals() is live from 2026-08-22, so an un-gated
  // log here runs on every production page load.
  if (import.meta.env.DEV) console.log(`📊 Web Vital - ${name}:`, {
    value: Math.round(name === 'CLS' ? value * 1000 : value),
    rating,
    id,
    threshold: WEB_VITALS_THRESHOLDS[name as keyof typeof WEB_VITALS_THRESHOLDS]
  });
}

// Determine rating based on thresholds
function getRating(name: string, value: number): 'good' | 'needs-improvement' | 'poor' {
  const thresholds = WEB_VITALS_THRESHOLDS[name as keyof typeof WEB_VITALS_THRESHOLDS];
  if (!thresholds) return 'good';
  
  if (value <= thresholds.good) return 'good';
  if (value <= thresholds.needsImprovement) return 'needs-improvement';
  return 'poor';
}

// Initialize all Web Vitals monitoring
export function initializeWebVitals() {
  if (import.meta.env.DEV) console.log('🚀 Initializing Core Web Vitals monitoring...');
  
  try {
    // Largest Contentful Paint (LCP) - measures loading performance
    getLCP((metric) => {
      sendToAnalytics({
        name: 'LCP',
        value: metric.value,
        id: metric.id,
        rating: getRating('LCP', metric.value)
      });
    });

    // First Input Delay (FID) - measures interactivity
    getFID((metric) => {
      sendToAnalytics({
        name: 'FID',
        value: metric.value,
        id: metric.id,
        rating: getRating('FID', metric.value)
      });
    });

    // Cumulative Layout Shift (CLS) - measures visual stability
    getCLS((metric) => {
      sendToAnalytics({
        name: 'CLS',
        value: metric.value,
        id: metric.id,
        rating: getRating('CLS', metric.value)
      });
    });

    // First Contentful Paint (FCP) - measures perceived loading
    getFCP((metric) => {
      sendToAnalytics({
        name: 'FCP',
        value: metric.value,
        id: metric.id,
        rating: getRating('FCP', metric.value)
      });
    });

    // Time to First Byte (TTFB) - measures server response time
    getTTFB((metric) => {
      sendToAnalytics({
        name: 'TTFB',
        value: metric.value,
        id: metric.id,
        rating: getRating('TTFB', metric.value)
      });
    });
    
    if (import.meta.env.DEV) console.log('✅ Core Web Vitals monitoring initialized successfully');
  } catch (error) {
    console.error('❌ Error initializing Web Vitals:', error);
  }
}

// Performance monitoring for page load times
export function trackPageLoadPerformance() {
  if (typeof window === 'undefined' || !window.performance) return;
  
  // Wait for page to fully load
  window.addEventListener('load', () => {
    const perfData = window.performance.timing;
    const pageLoadTime = perfData.loadEventEnd - perfData.navigationStart;
    const domContentLoadedTime = perfData.domContentLoadedEventEnd - perfData.navigationStart;
    const firstByteTime = perfData.responseStart - perfData.navigationStart;
    
    // Send performance metrics
    trackGtagEvent(ANALYTICS_EVENTS.PAGE_LOAD_PERFORMANCE, {
      event_category: 'Performance',
      page_load_time: pageLoadTime,
      dom_content_loaded_time: domContentLoadedTime,
      first_byte_time: firstByteTime,
      non_interaction: true,
    });
    
    if (import.meta.env.DEV) console.log('⚡ Page Performance:', {
      pageLoadTime: `${pageLoadTime}ms`,
      domContentLoadedTime: `${domContentLoadedTime}ms`,
      firstByteTime: `${firstByteTime}ms`
    });
  });
}

// Initialize page load performance tracking
trackPageLoadPerformance();